/**
 * ErrorBoundary — catch React rendering errors with a friendly fallback.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Box, Typography, Button } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { withTranslation, type WithTranslation } from "react-i18next";
import { reportError } from "@/utils/errorReporter";
import { PageLoadError } from "@/utils/lazyPage";

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    reportError({
      severity: "fatal",
      errorType: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
      tags: { component: "ErrorBoundary" },
      extra: { componentStack: info.componentStack },
    });
  }

  handleReset = () => {
    // React.lazy retains rejected imports; a reload also picks up a newer deployment's chunks.
    if (this.state.error instanceof PageLoadError) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      const isPageLoadError = this.state.error instanceof PageLoadError;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "40vh",
            gap: 2,
            textAlign: "center",
            p: 3,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "error.main" }} />
          <Typography variant="h5">
            {this.props.t(isPageLoadError ? "errorBoundary.pageLoadTitle" : "errorBoundary.title")}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
            {isPageLoadError
              ? this.props.t("errorBoundary.pageLoadMessage")
              : this.state.error?.message ?? this.props.t("errorBoundary.message")}
          </Typography>
          {isPageLoadError && (
            <Typography color="text.secondary" sx={{ maxWidth: 400 }}>
              {this.props.t("errorBoundary.reloadWarning")}
            </Typography>
          )}
          <Button variant="contained" onClick={this.handleReset}>
            {this.props.t(isPageLoadError ? "errorBoundary.reload" : "errorBoundary.retry")}
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default withTranslation("common")(ErrorBoundary);
